import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Package, Receipt } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    succeeded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    refunded: 'bg-amber-50 text-amber-700 border-amber-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_LABELS = {
    fr: { succeeded: 'Payée', refunded: 'Remboursée', failed: 'Échouée' },
    en: { succeeded: 'Paid', refunded: 'Refunded', failed: 'Failed' },
};

export default function Orders() {
    const { lang } = useLang();
    const { session } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [orders, setOrders] = useState(null);

    useEffect(() => {
        if (!currentWorkspace || !session) return;
        axios.get(`${API}/orders`, {
            params: { workspace_id: currentWorkspace.id },
            headers: { Authorization: `Bearer ${session.access_token}` },
        }).then(r => setOrders(r.data.items || []))
            .catch(e => { toast.error(e.response?.data?.detail || e.message); setOrders([]); });
    }, [currentWorkspace, session]);

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">—</div>;

    return (
        <div className="space-y-8" data-testid="orders-page">
            <div>
                <div className="eyebrow mb-2">Marketplace · {lang === 'en' ? 'Orders' : 'Commandes'}</div>
                <h1 className="font-heading text-4xl">{lang === 'en' ? 'My orders' : 'Mes commandes'}</h1>
                <p className="text-foreground/70 mt-3 max-w-3xl">
                    {lang === 'en'
                        ? 'History of marketplace pack purchases for this workspace.'
                        : "Historique des achats de packs marketplace de cet espace de travail."}
                </p>
            </div>

            {orders === null ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>
            ) : orders.length === 0 ? (
                <div className="card-soft p-12 text-center text-foreground/60" data-testid="orders-empty">
                    <Package size={32} strokeWidth={1.4} className="mx-auto mb-3 text-muted-foreground" />
                    {lang === 'en' ? 'No orders yet. Visit the Marketplace to add packs to your cart.' : 'Aucune commande pour le moment. Rendez-vous sur la Marketplace pour ajouter des packs au panier.'}
                </div>
            ) : (
                <div className="space-y-4">
                    {orders.map(o => {
                        const items = o.payload?.items || [];
                        const statusLabel = (STATUS_LABELS[lang] || STATUS_LABELS.fr)[o.status] || o.status;
                        return (
                            <div key={o.id} className="card-soft p-6" data-testid={`order-${o.reference}`}>
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex items-center gap-3">
                                        <div className="h-11 w-11 rounded-xl bg-primary-light flex items-center justify-center">
                                            <Receipt size={18} strokeWidth={1.6} className="text-primary" />
                                        </div>
                                        <div>
                                            <div className="font-medium font-mono text-sm">{o.reference}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                {new Date(o.created_at).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR')}
                                                {o.author && <> · {lang === 'en' ? 'by' : 'par'} {o.author}</>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${STATUS_STYLES[o.status] || 'bg-stone-50 text-stone-600 border-stone-200'}`}
                                            data-testid={`order-status-${o.reference}`}>
                                            {statusLabel}
                                        </span>
                                        <div className="font-heading text-2xl text-primary">
                                            {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(o.amount)} {o.currency}
                                        </div>
                                    </div>
                                </div>
                                {items.length > 0 && (
                                    <ul className="mt-4 pt-4 border-t border-border space-y-1.5">
                                        {items.map(it => (
                                            <li key={it.slug} className="flex items-center justify-between text-sm">
                                                <span className="text-foreground/80">{it.name || it.slug}</span>
                                                <span className="text-muted-foreground">{new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(it.amount_eur)} €</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
